<template>
  <section class="page_management__content">
    <header class="content__header">
      <h2 v-html="$t('userInfo.lnb.pageManagement')"></h2>
    </header>

    <div class="content__body" ref="scrollContent">
      <div class="button__all" :class="{ disabled: !selectedApplyList.length }">
        <button
          class="approve"
          @click.stop="showDialog = 'approve'">
          <i class="icon icon--check-red-size20"></i>
          {{ $t('userInfo.pageManagement.approve.button') }}
        </button>
        <span class="gap"></span>
        <button
          class="reject"
          @click.stop="showDialog = 'reject'">
          <i class="icon icon--close-size20"></i>
          {{ $t('userInfo.pageManagement.reject.button') }}
        </button>
      </div>

      <div class="input__search">
        <input type="text"
               class="search-input"
               :value="searchKeyword"
               @input="searchKeyword = $event.target.value.trim().toLowerCase()">
        <i class="icon icon--search"></i>
      </div>

      <div v-if="hasResult">
        <content-box class="content__apply-list" :width="'100%'">
          <div class="item__body">
            <table>
              <colgroup>
                <col width="33px" v-if="applyList && applyList.length">
                <col width="108px">
                <col width="108px">
                <col :width="$i18n.locale === 'en' ? '118px' : '90px'">
                <col width="*">
                <col width="88px">
                <col width="98px">
                <col :width="$i18n.locale === 'en' ? '160px' : '140px'">
              </colgroup>
              <thead>
              <tr>
                <th class="data__checkbox" v-if="applyList && applyList.length">
                  <ui-checkbox :value="selectedApplyList.length === applyList.length"
                               @input="onCheckAllApply"></ui-checkbox>
                </th>
                <th class="align-left">
                  <sort-options
                    columnName="branchName"
                    :sortColumn="sortColumn"
                    :isSortAsc="isAsc"
                    @onSortOptionChange="onSortOptionChange">{{ $t('common.corporation') }}</sort-options>
                </th>
                <th class="align-left">
                  <sort-options
                    columnName="userId"
                    :sortColumn="sortColumn"
                    :isSortAsc="isAsc"
                    @onSortOptionChange="onSortOptionChange">{{ $t('common.id') }}</sort-options>
                </th>
                <th class="align-left">
                  <sort-options
                    columnName="siteCode"
                    :sortColumn="sortColumn"
                    :isSortAsc="isAsc"
                    @onSortOptionChange="onSortOptionChange">{{ $t('common.countryCode') }}</sort-options>
                </th>
                <th class="align-left">
                  <sort-options
                    columnName="pageName"
                    :sortColumn="sortColumn"
                    :isSortAsc="isAsc"
                    @onSortOptionChange="onSortOptionChange">
                    <span v-html="$t('common.page')"></span></sort-options>
                </th>
                <th class="align-left">
                  <sort-options
                    columnName="regDate"
                    :sortColumn="sortColumn"
                    :isSortAsc="isAsc"
                    @onSortOptionChange="onSortOptionChange">{{ $t('common.registered') }}</sort-options>
                </th>
                <th class="align-left">
                  {{ $t('common.analysisPeriod') }}
                </th>
                <th class="align-left"></th>
              </tr>
              </thead>
              <tbody v-if="hasResult && applyList && applyList.length">
                <tr
                  v-for="apply in applyList"
                  v-bind:key="apply._id"
                  v-show="!apply.isHide"
                  style="cursor: pointer"
                  @click="onDetail(apply)">
                  <td class="data__checkbox" @click.stop="">
                    <ui-checkbox :value="selectedApplyList.includes(apply._id)"
                                 @input="onCheckApply(apply._id)"></ui-checkbox>
                  </td>
                  <td class="align-left ellipsis">
                    <text-highlight :searchKeyword="searchKeyword"
                                    :textItem="apply.branchName"></text-highlight>
                  </td>
                  <td class="align-left ellipsis">
                    <text-highlight :searchKeyword="searchKeyword"
                                    :textItem="apply.userId"></text-highlight>
                  </td>
                  <td class="align-left ellipsis">
                    <text-highlight :searchKeyword="searchKeyword"
                                    :textItem="apply.siteCode"></text-highlight>
                  </td>
                  <td class="align-left">
                    <p class="page-name" :class="{ dup: apply.duplicated }">
                      <span class="duplicated" v-if="apply.duplicated">
                        <ui-tooltip class="ui-tooltip" small arrowCenter noApplyCenterAnimate>
                          {{ $t('userInfo.pageManagement.duplicatedUrl') }}
                        </ui-tooltip>
                        <i class="icon icon--documents"></i>
                      </span>
                      <text-highlight class="name"
                                      :searchKeyword="searchKeyword"
                                      :textItem="apply.pageName || 'untitled'"></text-highlight>
                    </p>
                    <p class="page-url">
                      <span class="match-type">{{ apply.matchType | matchType(true) }}</span>
                      <span class="url ellipsis" :title="apply.url">{{ apply.url }}</span>
                    </p>
                  </td>
                  <td class="align-left">
                    <p class="data__reg-date">
                      {{ apply.regDate | dateFormatTimezone(userData.timezoneName) }}
                    </p>
                  </td>
                  <td class="align-left">
                    <p class="data__analysis-date">
                      {{ (apply.startDate || apply.regDate) | dateFormatTimezone(userData.timezoneName) }}<br>
                      ~ <span v-if="apply.endDate">{{ apply.endDate | dateFormatTimezone(userData.timezoneName) }}</span>
                      <span v-else>{{ $t('common.nonStop') }}</span>
                    </p>
                  </td>
                  <td class="align-right">
                    <p class="data__confirm">
                      <ui-button
                        size="small"
                        @click.stop="onRejectConfirm(apply)">
                        {{ $t('userInfo.pageManagement.reject.button') }}
                      </ui-button>
                      <ui-button
                        size="small"
                        :primary2="true"
                        @click.stop="onApproveConfirm(apply)">
                        {{ $t('userInfo.pageManagement.approve.button') }}
                      </ui-button>
                    </p>
                  </td>
                </tr>
              </tbody>
              <tbody class="item__body" v-else-if="applyList && !applyList.length">
                <tr>
                  <td colspan="7" class="no-request">
                    {{ $t('userInfo.requestManagement.noRequestFound') }}
                  </td>
                </tr>
              </tbody>
            </table>

            <div class="load" v-if="!applyList">
              <ui-loading-circle>
                {{ $t('common.loading') }}
              </ui-loading-circle>
            </div>
          </div>
        </content-box>

        <back-to-top :refName="'scrollContent'" />
      </div>

      <div v-else class="content__body">
        <div class="no-result">
          <i class="icon icon--empty"></i>
          <p class="title">{{ $t('dashboard.noApplies.searchResult.title') }}</p>
          <p>{{ $t('dashboard.noApplies.searchResult.desc') }}</p>
          <p class="keyword">'{{ searchKeyword }}'</p>
        </div>
      </div>
    </div>

    <dimmed v-if="showDialog === 'detail'" class="page_detail">
      <page-management-detail
        :apply="selectedApply"
        @onCancel="showDialog = false"
        @onReject="showDialog = 'reject'"
        @onApprove="showDialog = 'approve'"></page-management-detail>
    </dimmed>

    <dimmed v-if="['approve', 'reject'].includes(showDialog)">
      <content-box class="page_approve_reject" :width="'430px'">
        <span class="icon" :class="`icon--${showDialog === 'approve' ? 'alert-document' : 'alert'}`"></span>
        <p class="title">{{ $t(`userInfo.pageManagement.${showDialog}.title`) }}</p>
        <p class="desc" v-html=" $t(`userInfo.pageManagement.${showDialog}.desc.${confirmDescType}`,
            { count: (selectedApply ? 1 : selectedApplyList.length) }) ">
        </p>
        <ui-button
          @click="showDialog = null">{{ $t('common.cancel') }}</ui-button>
        <ui-button :primary2="true"
                   @click="onConfirm">{{ $t('common.confirm') }}</ui-button>
      </content-box>
    </dimmed>

  </section>
</template>

<script>
import ContentBox from '@/components/ui/ContentBox'
import Dimmed from '@/components/ui/Dimmed'
import BackToTop from '@/components/ui/BackToTop'
import SortOptions from '@/components/ui/SortOptions'
import TextHighlight from '@/components/ui/TextHighlight'
import UiButton from '@/components/ui/UiButton'
import UiTooltip from '@/components/ui/UiTooltip'
import UiCheckbox from '@/components/ui/UiCheckbox'
import PageManagementDetail from '@/components/user-info/page-management/PageManagementDetail'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle.vue'
import { pendingList, approve } from '@/api/apply'
import { mapState } from 'vuex'

export default {
  components: {
    ContentBox,
    Dimmed,
    BackToTop,
    SortOptions,
    TextHighlight,
    UiButton,
    UiTooltip,
    UiCheckbox,
    UiLoadingCircle,
    PageManagementDetail
  },
  data () {
    return {
      searchKeyword: '',
      hasResult: true,
      applyList: null,
      sortColumn: 'regDate',
      isAsc: false,
      selectedApplyList: [],
      selectedApply: null,
      showDialog: false
    }
  },
  computed: {
    ...mapState([
      'userData'
    ]),
    confirmDescType () {
      switch (this.$i18n.locale) {
        case 'ko':
        case 'en':
          if (this.selectedApply || this.selectedApplyList.length < 2) {
            return 'singular'
          }
          break
        case 'ja':
          if (this.selectedApply || this.selectedApplyList.length < 10) {
            return 'singular'
          }
          break
      }
      return 'plural'
    }
  },
  watch: {
    searchKeyword (value) {
      if (!value.length) {
        this.hasResult = true
      } else {
        this.hasResult = false
      }

      this.applyList.forEach(item => {
        item.isHide = false
        if (value.length) {
          if (!item.userId.toLowerCase().includes(value) &&
            !(item.branchName && item.branchName.toLowerCase().includes(value)) &&
            !item.siteCode.includes(value.toUpperCase()) &&
            !item.pageName.toLowerCase().includes(value)
          ) {
            item.isHide = true
          } else {
            this.hasResult = true
          }
        }
      })
    },
    showDialog (value) {
      if (!value) {
        this.selectedApply = null
      }
    }
  },
  methods: {
    onDetail (apply) {
      this.selectedApply = apply
      this.showDialog = 'detail'
    },
    onApproveConfirm (apply) {
      this.selectedApply = apply
      this.showDialog = 'approve'
    },
    onApprove () {
      approve({
        approve: true,
        aidList: this.selectedApply ? [ this.selectedApply._id ] : this.selectedApplyList
      }).then(result => {
        if (this.selectedApply) {
          this.removeItem(this.selectedApply._id)
        } else {
          this.selectedApplyList = []
        }
        this.showDialog = false
        this.load()
      })
    },
    onRejectConfirm (apply) {
      this.selectedApply = apply
      this.showDialog = 'reject'
    },
    onReject () {
      approve({
        approve: false,
        aidList: this.selectedApply ? [ this.selectedApply._id ] : this.selectedApplyList
      }).then(result => {
        if (this.selectedApply) {
          this.removeItem(this.selectedApply._id)
        } else {
          this.selectedApplyList = []
        }
        this.showDialog = false
        this.load()
      })
    },
    onConfirm () {
      return this.showDialog === 'reject' ? this.onReject() : this.onApprove()
    },
    onCheckApply (aid) {
      if (this.selectedApplyList.includes(aid)) {
        this.removeItem(aid)
      } else {
        this.selectedApplyList.push(aid)
      }
    },
    removeItem (aid) {
      const idx = this.selectedApplyList.indexOf(aid)
      if (idx >= 0) {
        this.selectedApplyList.splice(idx, 1)
      }
    },
    onCheckAllApply () {
      if (this.selectedApplyList.length === this.applyList.length) {
        this.selectedApplyList = []
      } else {
        this.selectedApplyList = []
        this.applyList.forEach(apply => {
          this.selectedApplyList.push(apply._id)
        })
      }
    },
    onSortOptionChange (columnName, isAsc) {
      this.applyList.sort(function (a, b) {
        return String(a[columnName]).localeCompare(String(b[columnName]))
      })
      if (!isAsc) {
        this.applyList.reverse()
      }
      this.sortColumn = columnName
      this.isAsc = isAsc
    },
    onUserDetailBackButton () {
      this.selectedUser = null
      this.load()
    },
    load () {
      pendingList().then(result => {
        this.applyList = result
        this.applyList.forEach(item => {
          item.isHide = false
        })
        this.onSortOptionChange(this.sortColumn, this.isAsc)
      })
    }
  },
  created () {
    this.load()
  }
}
</script>

<style lang="scss" scoped="scoped">
.page_management__content {
  position: relative;
  float: left;
  width: calc(100% - 211px);
  margin-top: 40px;
  box-sizing: border-box;

  .content__header {
    font-size: 26px;
    font-weight: bold;
    color: #444444;
    margin-left: 30px;
    margin-right: 30px;
    padding-bottom: 22px;

    h2 {
      padding-left: 10px;
    }
  }

  .content__body {
    position: relative;
    height: calc(100vh - 136px) !important;
    width: calc(100% - 60px) !important;
    margin: 1px 30px 0;
    overflow-y: auto;
    border-top: 1px solid #d7d7d7;
    padding: 1px;

    & /deep/ .content-box {
      padding: 25px;
    }

    .load {
      height: 440px;
      border-bottom: 1px solid #dddddd;
      position: relative;

      .loading {
        @include positionXYcenter;
      }
    }

    .button__all {
      display: inline-block;
      border-radius: 7px;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
      background-color: #ffffff;
      margin: 18px 0;

      &.disabled {
        button {
          opacity: 0.3;
        }
      }

      button {
        vertical-align: top;
        font-size: 13px;
        font-weight: bold;
        color: #444;
        padding: 10px 20px;
        height: 32px;
        box-sizing: border-box;
        transition: opacity 0.15s ease;

        i {
          vertical-align: middle;
          transform: translateY(-1px);
        }

        .approve {
        }
      }

      .gap {
        display: inline-block;
        width: 1px;
        height: 12px;
        background: #d7d7d7;
        margin: 14px 0 12px 0;
      }
    }

    .input__search {
      position: absolute;
      right: 0;
      top: 20px;

      .search-input {
        width: 240px;
        height: 38px;
        line-height: 36px;
        border-radius: 20px;
        border: solid 1px #bbbbbb;
        background-color: #ffffff;
        padding-left: 18px;
        padding-right: 26px;
        box-sizing: border-box;
        font-size: 14px;
        color: #2f2f2f;

        &:focus {
          border: solid 1px #289eef;
        }
      }

      .icon {
        position: absolute;
        right: 11px;
        transform: translateY(-50%);
        top: 18px;
      }
    }
  }

  .content__apply-list {
    position: relative;

    .input__search {
      position: absolute;
      right: 20px;
      top: 13px;

      .search-input {
        width: 240px;
        height: 38px;
        line-height: 36px;
        border-radius: 20px;
        border: solid 1px #bbbbbb;
        background-color: #ffffff;
        padding-left: 18px;
        padding-right: 26px;
        box-sizing: border-box;
        font-size: 14px;
        color: #2f2f2f;

        &:focus {
          border: solid 1px #289eef;
        }
      }

      .icon {
        position: absolute;
        right: 11px;
        transform: translateY(-50%);
        top: 18px;
      }
    }

    .item__body {
      table {
        font-size: 13px;
        width: 100%;
        table-layout: fixed;
      }

      table thead tr {
        height: 50px;
        background-color: #555555;
        line-height: 1.23;
        color: #ffffff;
        text-align: center;

        th {
          white-space: nowrap;
          overflow: hidden;
        }

        th:nth-of-type(1) {
          position: relative;
          overflow: visible;

          &:before {
            content: "";
            display: inline-block;
            width: 15px;
            height: 50px;
            position: absolute;
            top: 0;
            left: -5px;
            background-color: #555;
            border-radius: 5px;
          }
        }

        th:nth-last-of-type(1) {
          position: relative;
          overflow: visible;

          &:after {
            content: "";
            display: inline-block;
            width: 10px;
            height: 50px;
            position: absolute;
            top: 0;
            right: -5px;
            background-color: #555;
            border-radius: 5px;
          }
        }

        .align-left {
          text-align: left;
          padding-left: 10px;
        }

        .align-right {
          text-align: right;
          padding-right: 2px;
        }

        .data__checkbox {
          .ui_checkbox {
            margin-left: 10px !important;
          }
        }
      }

      table tbody tr {
        height: 80px;
        text-align: center;
        border-bottom: 1px solid #ebebeb;
        box-sizing: border-box;
        color: #2f2f2f;

        &:nth-of-type(1) {
          border-top: none;
        }

        &:nth-last-of-type(1) {
          border-bottom: 1px solid #ebebeb;
        }

        &:not(.deleted):hover {
          background-color: #f4f4f4;
        }

        &.checked:not(.deleted) {
          border: solid 1px #289eef;
          outline: solid 3px rgba(40, 158, 239, 0.18);
        }

        .align-left {
          text-align: left;
          padding-left: 10px;
          box-sizing: border-box;
        }

        .align-right {
          text-align: right;
          padding-right: 5px;
          box-sizing: border-box;
        }

        .no-request {
          color: #767676;
          height: 236px;
          background: #ffffff;
        }

        .page-name {
          font-size: 14px;
          font-weight: bold;
          line-height: 1;
          color: #2f2f2f;
          height: 16px;
          margin-bottom: -2px;
          width: 95%;
          position: relative;

          .icon {
            vertical-align: middle;
            margin-right: 3px;
          }

          .name {
            @include textEllipsis;
            height: 16px;
            line-height: 16px;
            display: block;
          }

          &.dup .name {
            padding-left: 20px;
          }

          .duplicated {
            position: absolute;
            display: inline;
            top: 0;
            left: 0;

            .ui-tooltip {
              @include positionXcenter;
              position: absolute;
              top: -48px;
              margin-left: -5px;
              padding: 10px 15px;
              display: none;
              font-weight: normal;
              font-size: 13px;
              color: #444444;
            }

            &:hover .ui-tooltip {
              display: block;
            }
          }
        }

        .page-url {
          margin-top: 14px;
          font-size: 13px;
          line-height: 1;
          color: #666666;
          vertical-align: text-bottom;
          overflow: hidden;

          span {
            height: 15px;

            &.match-type {
              display: inline-block;
              margin-right: 8px;
              font-size: 12px;
              font-weight: bold;
              line-height: 15px;
              height: 15px;
              color: #ffffff;
              padding: 1px 5px;
              border-radius: 4px;
              background-color: #8d8d8d;
            }

            &.url {
              width: calc(100% - 70px);
              vertical-align: middle;
              display: inline-block;
            }
          }
        }

        .data__checkbox {
          .ui_checkbox {
            margin-left: 10px !important;
          }
        }

        .data__reg-date {
          color: #666;
        }

        .data__analysis-date {
          color: #666;
          line-height: 1.21;
        }

        .data__confirm {
          button {
            margin-left: 5px;
          }
        }
      }
    }
  }

  .no-result {
    @include positionYcenter;
    text-align: center;
    font-size: 14px;
    color: #767676;
    width: 100%;

    .title {
      margin: 15px 0;
      font-size: 20px;
      font-weight: 500;
      color: #2f2f2f;
    }
    .keyword {
      font-size: 15px;
      line-height: 24px;
      font-weight: 500;
      color: #ff8400;
      margin: 30px auto 0;
      width: 524px;
      word-break: break-all;
    }
  }

  .page_approve_reject {
    padding-top: 50px;
    padding-bottom: 25px;
    text-align: center;

    .title {
      font-size: 20px;
      font-weight: bold;
      color: #444444;
      margin-top: 15px;
    }
    .desc {
      font-size: 14px;
      line-height: 1.43;
      color: #2f2f2f;
      margin-top: 12px;
      height: 50px;
    }
    .ui-btn {
      margin: 30px 5px 0;
    }
  }
}
</style>
